import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { SelectChangeEvent } from "@mui/material/Select";
import CommonModal from "../common/CommonModal";
import { useTranslation } from "react-i18next";
import { useTenantHeader } from "hooks/useTenantHeader";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useStudents, Student } from "../../hooks/useStudents";
import { useAttendance, MonthType } from "../../hooks/useAttendance";
import { useAuth } from "../../hooks/useAuth";
import { useTeacher } from "../../hooks/useTeacher";
import { format, parseISO, isValid } from "date-fns";
import { useHolidays } from "../../hooks/useHolidays";
import { useStudentUpdateRequest } from "../../hooks/useStudentUpdateRequest";
import { useError } from "../../contexts/ErrorContext";
import { useQueryClient } from "@tanstack/react-query";

interface AttendanceFormModalProps {
  open: boolean;
  onClose: () => void;
  initialTeacherId?: number | null;
  initialDate?: string | null; // yyyy-MM-dd (used to derive month/year for edit)
}

type AttendanceCode = "PRESENT" | "ABSENT" | "LEAVE" | "HALF_DAY" | "";

const MONTH_OPTIONS: { value: MonthType; labelKey: string }[] = [
  { value: "JANUARY", labelKey: "january" },
  { value: "FEBRUARY", labelKey: "february" },
  { value: "MARCH", labelKey: "march" },
  { value: "APRIL", labelKey: "april" },
  { value: "MAY", labelKey: "may" },
  { value: "JUNE", labelKey: "june" },
  { value: "JULY", labelKey: "july" },
  { value: "AUGUST", labelKey: "august" },
  { value: "SEPTEMBER", labelKey: "september" },
  { value: "OCTOBER", labelKey: "october" },
  { value: "NOVEMBER", labelKey: "november" },
  { value: "DECEMBER", labelKey: "december" },
];

const parseIsoDay = (iso?: string) => {
  if (!iso) return null;
  // expects yyyy-MM-dd
  const parts = iso.split("-");
  if (parts.length !== 3) return null;
  const day = Number(parts[2]);
  return Number.isFinite(day) ? day : null;
};

const parsePendingRequestDate = (fieldName: string) => {
  if (!fieldName || !fieldName.startsWith("Attendance (")) return null;
  const dateStr = fieldName.substring(fieldName.indexOf("(") + 1, fieldName.indexOf(")"));
  const parts = dateStr.split(/[-/]/);
  if (parts.length !== 3) return null;
  const day = Number(parts[0]);
  const monthNum = Number(parts[1]);
  const year = Number(parts[2]);
  return { day, monthNum, year };
};

const AttendanceFormModal: React.FC<AttendanceFormModalProps> = ({
  open,
  onClose,
  initialTeacherId,
  initialDate,
}) => {
  const { t } = useTranslation();
  const { user, isTeacher } = useAuth();
  const { selectedSession, selectedClass } = useSessionClass();
  const { tenantLoading } = useTenantHeader();

  const schoolId = user?.schoolId;
  const { teachersQuery } = useTeacher(schoolId ?? 0, tenantLoading);
  const { useStudentsBySessionIdAndClassId } = useStudents(tenantLoading);
  const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);
  const { bulkAttendanceMutation, attendanceStatusQuery, getAttendanceQuery } = useAttendance(
    selectedClass?.id && selectedSession?.id
      ? {
          classId: selectedClass.id,
          sessionId: selectedSession.id,
          month: undefined,
          year: undefined,
        }
      : undefined
  );

  const now = new Date();
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | "">("");
  const [month, setMonth] = useState<MonthType>(MONTH_OPTIONS[now.getMonth()]?.value || "JANUARY");
  const [year, setYear] = useState<number>(now.getFullYear());
  const [teacherTouched, setTeacherTouched] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  const getSelectSx = (minWidth?: number) => ({
    width: "100%",
    ...(minWidth && { minWidth }),
    "& .MuiOutlinedInput-root": {
      height: 44,
      background: "var(--gray-100, #F8F9FA)",
      borderRadius: "8px",
      "& fieldset": {
        border: "1px solid var(--gray-200, #E9ECEF)",
      },
      "&:hover fieldset": {
        border: "1px solid var(--gray-200, #E9ECEF)",
      },
      "&.Mui-focused fieldset": {
        border: "1px solid #1976d2",
      },
      "&.Mui-disabled fieldset": {
        border: "1px solid var(--gray-200, #E9ECEF)",
        opacity: 0.8,
      },
    },
    "& .MuiSelect-select": { display: "flex", alignItems: "center" },
  });

  const [originalAttendance, setOriginalAttendance] = useState<Record<number, Record<number, AttendanceCode>>>({});
  const { createStudentUpdateRequestMutation, useStudentPendingRequests } = useStudentUpdateRequest();
  const { showError, showSuccess } = useError();
  const queryClient = useQueryClient();
  const studentUpdateRequestsQuery = useStudentPendingRequests(open);

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ["student-attendance"] });
      queryClient.invalidateQueries({ queryKey: ["student-update-requests"] });
    }
  }, [open, queryClient]);

  const studentsQuery = useStudentsBySessionIdAndClassId(selectedSession?.id, selectedClass?.id);

  const students = useMemo(() => {
    const list = studentsQuery.data || [];
    return list
      .filter((s) => s.status !== false)
      .filter((s) =>
        studentSearchTerm === "" ||
        (s.studentName || "").toLowerCase().includes(studentSearchTerm.toLowerCase())
      )
      .slice()
      .sort((a, b) =>
        (a.studentName || "").toLowerCase().localeCompare((b.studentName || "").toLowerCase())
      );
  }, [studentsQuery.data, studentSearchTerm]);

  const editDay = useMemo(() => {
    return initialDate ? parseIsoDay(initialDate) : null;
  }, [initialDate]);

  const days = useMemo(() => {
    if (editDay !== null) {
      return [editDay];
    }
    return Array.from({ length: 31 }, (_, i) => i + 1);
  }, [editDay]);

  const [attendance, setAttendance] = useState<Record<number, Record<number, AttendanceCode>>>({});

  const daysWithExistingAttendance = useMemo(() => {
    const takenDays = new Set<number>();
    Object.values(originalAttendance).forEach((studentDays) => {
      Object.keys(studentDays).forEach((dayStr) => {
        const day = Number(dayStr);
        if (studentDays[day] && (studentDays[day] as string) !== "NOT-MARKED") {
          takenDays.add(day);
        }
      });
    });
    return takenDays;
  }, [originalAttendance]);

  const pendingMap = useMemo(() => {
    const map: Record<number, Record<number, { newValue: string; oldValue: string }>> = {};
    const list = studentUpdateRequestsQuery.data || [];
    
    const MONTH_MAP: Record<string, number> = {
      JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
      JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
    };
    const selectedMonthNum = MONTH_MAP[month] || 1;

    list.forEach((req) => {
      if (req.status !== "PENDING") return;
      const studentId = req.studentId;
      if (!studentId) return;

      (req.changes || []).forEach((change) => {
        const parsed = parsePendingRequestDate(change.fieldName);
        if (!parsed) return;
        if (parsed.monthNum === selectedMonthNum && parsed.year === year) {
          if (!map[studentId]) {
            map[studentId] = {};
          }
          map[studentId][parsed.day] = {
            newValue: change.newValue,
            oldValue: change.oldValue
          };
        }
      });
    });

    return map;
  }, [studentUpdateRequestsQuery.data, month, year]);

  const isHolidayOrSunday = useMemo(() => {
    const holidays = useholidaysQueryBySession.data || [];
    const classId = selectedClass?.id || 0;
    const mIdx = (() => {
      const list: MonthType[] = [
        "JANUARY",
        "FEBRUARY",
        "MARCH",
        "APRIL",
        "MAY",
        "JUNE",
        "JULY",
        "AUGUST",
        "SEPTEMBER",
        "OCTOBER",
        "NOVEMBER",
        "DECEMBER",
      ];
      return Math.max(0, list.indexOf(month as any));
    })();

    return (day: number) => {
      const d = new Date(year, mIdx, day);
      if (isNaN(d.getTime())) return false;
      // Disallow future dates for attendance marking
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dd = new Date(d);
      dd.setHours(0, 0, 0, 0);
      if (dd > today) return true;
      if (d.getDay() === 0) return true; // Sunday

      const dateStr = format(d, "dd-MM-yyyy");
      return holidays.some((h) => {
        if (!h.dates?.includes(dateStr)) return false;
        if (h.forAllClasses) return true;
        return (h.classIds || []).includes(classId);
      });
    };
  }, [useholidaysQueryBySession.data, selectedClass?.id, month, year]);

  // Fetch attendance for month/year (using existing query function but overriding params)
  const monthAttendanceQuery = useAttendance(
    selectedClass?.id && selectedSession?.id
      ? {
          classId: selectedClass.id,
          sessionId: selectedSession.id,
          month,
          year,
        }
      : undefined
  ).getAttendanceQuery;

  useEffect(() => {
    if (!open) return;

    if (isTeacher()) {
      const userId = user?.id;
      const defaultTeacher = userId ? teachersQuery.data?.find((tt) => tt.userId === userId) : undefined;
      setSelectedTeacherId(defaultTeacher?.id ?? "");
      return;
    }

    // When editing, prefer the teacher from the selected record.
    if (!teacherTouched && initialTeacherId) {
      setSelectedTeacherId(Number(initialTeacherId));
      return;
    }

    // Default teacher: match logged-in user -> teacher.userId
    if (!teacherTouched) {
      const userId = user?.id;
      const defaultTeacher = userId ? teachersQuery.data?.find((tt) => tt.userId === userId) : undefined;
      setSelectedTeacherId(defaultTeacher?.id ?? "");
    }
  }, [open, teachersQuery.data, user?.id, initialTeacherId, teacherTouched, isTeacher]);

  useEffect(() => {
    if (!open) return;
    if (!initialDate) return;

    try {
      const d = parseISO(initialDate);
      if (!isValid(d)) return;
      const mIndex = d.getMonth();
      const nextMonth = MONTH_OPTIONS[mIndex]?.value;
      if (nextMonth) setMonth(nextMonth);
      setYear(d.getFullYear());
    } catch {
      // ignore
    }
  }, [open, initialDate]);

  useEffect(() => {
    if (!open) return;
    // Reset grid when month/year changes, then attempt to prefill from fetched data
    const next: Record<number, Record<number, AttendanceCode>> = {};
    for (const s of students) next[s.id!] = {};

    const data = monthAttendanceQuery.data || [];
    const statusIdToCode = new Map<number, AttendanceCode>(
      (attendanceStatusQuery.data || []).map((s) => [s.id, String(s.statusCode).toUpperCase() as AttendanceCode])
    );

    data.forEach((r) => {
      const studentId = r.studentId;
      if (!studentId) return;
      if (!next[studentId]) next[studentId] = {};

      // New API shape: dayStatusMap { "1": 1, "2": null, ... }
      if (r.dayStatusMap && typeof r.dayStatusMap === "object") {
        Object.entries(r.dayStatusMap as Record<string, number | null>).forEach(([dayStr, statusId]) => {
          const day = Number(dayStr);
          if (!Number.isFinite(day) || day < 1 || day > 31) return;
          if (statusId == null) return;
          const code = statusIdToCode.get(Number(statusId)) || "";
          if (!code) return;
          next[studentId][day] = code;
        });
        return;
      }

      // Legacy fallback: attendanceDate + statusCode
      const d = parseIsoDay(r.attendanceDate);
      if (!d) return;
      const code = (r.statusCode as AttendanceCode) || "";
      next[studentId][d] = code;
    });

    setAttendance(JSON.parse(JSON.stringify(next)));
    setOriginalAttendance(JSON.parse(JSON.stringify(next)));
  }, [open, month, year, students, monthAttendanceQuery.data, attendanceStatusQuery.data]);

  const handleTeacherChange = (e: SelectChangeEvent) => {
    const v = e.target.value;
    setTeacherTouched(true);
    setSelectedTeacherId(v === "" ? "" : Number(v));
  };

  const handleMonthChange = (e: SelectChangeEvent) => {
    setMonth(e.target.value as MonthType);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    if (!Number.isFinite(v)) return;
    setYear(v);
  };

  const setCell = (studentId: number, day: number, code: AttendanceCode) => {
    if (isHolidayOrSunday(day)) return;
    setAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [day]: code,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!selectedSession?.id || !selectedClass?.id) return;
    if (!selectedTeacherId) return;

    const statusMap = new Map(
      (attendanceStatusQuery.data || []).map((s) => [String(s.statusCode).toUpperCase(), s.id])
    );

    const immediateItems: { studentId: number; day: number; statusId: number }[] = [];
    const pendingChangesByStudent: Record<number, { fieldName: string; oldValue: string; newValue: string }[]> = {};

    const MONTH_MAP: Record<string, number> = {
      JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
      JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
    };

    const monthNum = MONTH_MAP[month] || 1;

    Object.entries(attendance).forEach(([sid, daysMap]) => {
      const studentId = Number(sid);
      Object.entries(daysMap || {}).forEach(([d, code]) => {
        const day = Number(d);
        if (isHolidayOrSunday(day)) return;

        const newCode = (code || "").toUpperCase();
        const oldCode = (originalAttendance[studentId]?.[day] || "").toUpperCase();

        if (newCode !== oldCode) {
          const statusId = statusMap.get(newCode) || 0;
          if (oldCode === "" || oldCode === "NOT-MARKED") {
            // New entry: save immediately
            if (statusId) {
              immediateItems.push({ studentId, day, statusId });
            }
          } else {
            // Edit of an existing entry: goes to approval flow
            if (newCode) {
              const formattedDate = `${String(day).padStart(2, '0')}-${String(monthNum).padStart(2, '0')}-${year}`;
              const fieldName = `Attendance (${formattedDate})`;
              if (!pendingChangesByStudent[studentId]) {
                pendingChangesByStudent[studentId] = [];
              }
              pendingChangesByStudent[studentId].push({
                fieldName,
                oldValue: oldCode,
                newValue: newCode
              });
            }
          }
        }
      });
    });

    const hasImmediate = immediateItems.length > 0;
    const hasPending = Object.keys(pendingChangesByStudent).length > 0;

    if (!hasImmediate && !hasPending) {
      showError("No changes detected.");
      return;
    }

    let immediateSuccess = false;
    let pendingSuccess = false;
    let apiError: any = null;

    // 1. Submit immediate updates
    if (hasImmediate) {
      try {
        await bulkAttendanceMutation.mutateAsync({
          staffId: Number(selectedTeacherId),
          classId: selectedClass.id,
          sessionId: selectedSession.id,
          month,
          year,
          students: immediateItems,
        });
        immediateSuccess = true;
      } catch (err: any) {
        apiError = err;
      }
    }

    // 2. Submit pending updates
    let hasDirectlyApplied = false;
    let hasSentForApproval = false;
    if (hasPending && (!hasImmediate || immediateSuccess)) {
      try {
        const requests = Object.entries(pendingChangesByStudent).map(async ([studentIdStr, changes]) => {
          const studentId = Number(studentIdStr);
          const response: any = await createStudentUpdateRequestMutation.mutateAsync({
            studentId,
            changes,
            requestedBy: user?.id,
            requestedType: 'TEACHER',
          });
          // Check returned DTOs for status to determine if applied directly or sent for approval
          const responseList = Array.isArray(response) ? response : [response];
          responseList.forEach((dto: any) => {
            if (dto?.status === 'APPROVED') {
              hasDirectlyApplied = true;
            } else if (dto?.status === 'PENDING') {
              hasSentForApproval = true;
            }
          });
        });
        await Promise.all(requests);
        pendingSuccess = true;
      } catch (err: any) {
        apiError = err;
      }
    }

    if (apiError) {
      const apiMessage = apiError?.response?.data?.message;
      showError(apiMessage || t('failed_to_submit_attendance'));
      return;
    }

    // 3. Success notification — message based on what actually happened
    const messages: string[] = [];
    if (immediateSuccess) {
      messages.push("New attendance saved successfully.");
    }
    if (hasDirectlyApplied) {
      messages.push("Attendance updated successfully.");
    }
    if (hasSentForApproval) {
      messages.push("Attendance edits submitted to Principal for approval.");
    }
    if (messages.length > 0) {
      showSuccess(messages.join(" "));
    } else if (immediateSuccess) {
      showSuccess("Attendance saved successfully.");
    }

    onClose();
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={t("attendance_register")}
      maxWidth={editDay !== null ? "sm" : "xl"}
      disableEscapeKeyDown
      contentSx={{ pt: 3, overflow: editDay !== null ? "hidden" : "auto" }}
      customActions={
        <>
          <Button onClick={onClose} variant="outlined" sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleSubmit}
            disabled={!selectedTeacherId || bulkAttendanceMutation.isLoading || attendanceStatusQuery.isLoading || students.length === 0}
          >
            {bulkAttendanceMutation.isLoading ? <CircularProgress size={22} sx={{ color: "common.white" }} /> : t("save")}
          </Button>
        </>
      }
    >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: editDay !== null ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
            },
            gap: 2,
            m: 2,
            overflow: "visible",
          }}
        >
          {editDay === null && (
            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">{t("session")}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ height: 44, display: "flex", alignItems: "center" }}>
                {selectedSession?.name || t("n/a")}
              </Typography>
            </Box>
          )}
          {editDay === null && (
            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">{t("class")}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ height: 44, display: "flex", alignItems: "center" }}>
                {selectedClass ? `${selectedClass.name} - ${selectedClass.section}` : t("n/a")}
              </Typography>
            </Box>
          )}

          <FormControl
            size="small"
            sx={{
              ...getSelectSx(),
              gridColumn: {
                xs: "span 1",
                sm: "span 2",
                md: editDay !== null ? "span 2" : "span 1",
              },
            }}
          >
            <InputLabel id="teacher-name-label">{t("teacher_name")}</InputLabel>
            <Select
              labelId="teacher-name-label"
              label={t("teacher_name")}
              value={selectedTeacherId === "" ? "" : String(selectedTeacherId)}
              onChange={handleTeacherChange}
              disabled={isTeacher()}
            >
              {!isTeacher() && <MenuItem value="">{t("select")}</MenuItem>}
              {teachersQuery.data
                ?.filter((teacher) => !isTeacher() || teacher.userId === user?.id)
                ?.map((teacher) => (
                  <MenuItem key={teacher.id} value={String(teacher.id)}>
                    {teacher.firstName} {teacher.lastName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={getSelectSx()}
          >
            <InputLabel id="month-label">{t("month")}</InputLabel>
            <Select
              labelId="month-label"
              label={t("month")}
              value={month}
              onChange={handleMonthChange}
            >
              {MONTH_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {t(m.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={getSelectSx()}
          >
            <InputLabel id="year-label">{t("year")}</InputLabel>
            <Select
              labelId="year-label"
              label={t("year")}
              value={String(year)}
              onChange={(e) => handleYearChange({ target: { value: e.target.value } } as any)}
            >
              {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ minHeight: "auto", width: "100%" }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxWidth: editDay !== null ? 500 : "100%",
              mx: "auto",
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "#fcfcfc" }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t("search_student", "Search Student")}
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiInputBase-root": { height: 40 },
                }}
              />
            </Box>
            <TableContainer
              sx={{
                maxHeight: editDay !== null ? "150px" : "calc(80vh - 280px)",
                overflowX: editDay !== null ? "hidden" : "auto",
                overflowY: "scroll",
                "&::-webkit-scrollbar": { width: "8px", height: "8px" },
                "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.35)", borderRadius: "4px" },
                "&::-webkit-scrollbar-track": { bgcolor: "rgba(0,0,0,0.05)" },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  width: "100%",
                  minWidth: editDay !== null ? "auto" : 1550,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        bgcolor: "#f5f7fb",
                        fontWeight: 800,
                        minWidth: 240,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        borderBottom: "1px solid",
                      }}
                    >
                      {t("student")}
                    </TableCell>
                    {days.map((d) => (
                      <TableCell
                        key={d}
                        align="center"
                        sx={{
                          bgcolor: "#f5f7fb",
                          fontWeight: 800,
                          width: 78,
                          borderLeft: "1px solid",
                          borderColor: "divider",
                          borderBottom: "1px solid",
                        }}
                      >
                        {d}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: "background.paper",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          minWidth: 240,
                          borderBottom: "1px solid",
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {s.studentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.enrolmentNo || ""}
                        </Typography>
                      </TableCell>
                      {days.map((d) => {
                        const pending = pendingMap[s.id!]?.[d];
                        const value = pending ? pending.newValue : (attendance[s.id!]?.[d] || "");
                        const isHoliday = isHolidayOrSunday(d);
                        
                        const hasExisting = daysWithExistingAttendance.has(d);
                        const isAlreadyTaken = !initialDate && hasExisting;
                        
                        const disabledDay = initialDate ? !!pending : (isHoliday || !!pending || isAlreadyTaken);
                        return (
                          <TableCell
                            key={d}
                            align="center"
                            sx={{
                              px: 0.75,
                              py: 0.75,
                              borderLeft: "1px solid",
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              bgcolor: pending 
                                ? "rgba(0, 0, 0, 0.06)" 
                                : isHoliday 
                                  ? "rgba(244, 67, 54, 0.03)" 
                                  : isAlreadyTaken
                                    ? "rgba(0, 0, 0, 0.04)"
                                    : "transparent",
                              position: "relative",
                            }}
                          >                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={value}
                              onChange={(_e, v) => setCell(s.id!, d, (v || "") as AttendanceCode)}
                              disabled={disabledDay}
                              sx={{
                                gap: "8px",
                                "& .MuiToggleButtonGroup-grouped": {
                                  border: "1px solid !important",
                                  borderRadius: "50% !important",
                                  minWidth: "28px !important",
                                  width: "28px !important",
                                  height: "28px !important",
                                  margin: "0 !important",
                                  padding: "0 !important",
                                  fontSize: "11px",
                                  fontWeight: 800,
                                }
                              }}
                            >
                              <ToggleButton
                                value="PRESENT"
                                sx={{
                                  color: pending ? "text.disabled" : "success.main",
                                  borderColor: pending ? "divider !important" : "success.light",
                                  bgcolor: pending ? "rgba(0, 0, 0, 0.05) !important" : "#fff",
                                  "&.Mui-selected": { bgcolor: "success.main", color: "common.white", borderColor: "success.main" },
                                  "&.Mui-selected:hover": { bgcolor: "success.dark" },
                                  "&:hover": { bgcolor: "rgba(46, 125, 50, 0.08)" }
                                }}
                              >
                                P
                              </ToggleButton>
                              <ToggleButton
                                value="ABSENT"
                                sx={{
                                  color: pending ? "text.disabled" : "error.main",
                                  borderColor: pending ? "divider !important" : "error.light",
                                  bgcolor: pending ? "rgba(0, 0, 0, 0.05) !important" : "#fff",
                                  "&.Mui-selected": { bgcolor: "error.main", color: "common.white", borderColor: "error.main" },
                                  "&.Mui-selected:hover": { bgcolor: "error.dark" },
                                  "&:hover": { bgcolor: "rgba(211, 47, 47, 0.08)" }
                                }}
                              >
                                A
                              </ToggleButton>
                              <ToggleButton
                                value="LEAVE"
                                sx={{
                                  color: pending ? "text.disabled" : "warning.main",
                                  borderColor: pending ? "divider !important" : "warning.light",
                                  bgcolor: pending ? "rgba(0, 0, 0, 0.05) !important" : "#fff",
                                  "&.Mui-selected": { bgcolor: "warning.main", color: "common.white", borderColor: "warning.main" },
                                  "&.Mui-selected:hover": { bgcolor: "warning.dark" },
                                  "&:hover": { bgcolor: "rgba(237, 108, 2, 0.08)" }
                                }}
                              >
                                L
                              </ToggleButton>
                              <ToggleButton
                                value="HALF_DAY"
                                sx={{
                                  color: pending ? "text.disabled" : "info.main",
                                  borderColor: pending ? "divider !important" : "info.light",
                                  bgcolor: pending ? "rgba(0, 0, 0, 0.05) !important" : "#fff",
                                  "&.Mui-selected": { bgcolor: "info.main", color: "common.white", borderColor: "info.main" },
                                  "&.Mui-selected:hover": { bgcolor: "info.dark" },
                                  "&:hover": { bgcolor: "rgba(2, 136, 209, 0.08)" }
                                }}
                              >
                                H
                              </ToggleButton>
                            </ToggleButtonGroup>
                            {pending && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 1,
                                  right: 1,
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: "warning.main",
                                }}
                                title={`Pending approval (requested: ${pending.newValue})`}
                              />
                            )}
                            {isAlreadyTaken && !pending && !isHoliday && (
                               <Box
                                 sx={{
                                   position: "absolute",
                                   top: 1,
                                   right: 1,
                                   width: 6,
                                   height: 6,
                                   borderRadius: "50%",
                                   bgcolor: "text.disabled",
                                 }}
                                 title={t("attendance_already_exist_for_this_date") || "Already Taken"}
                               />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider", bgcolor: "#fcfcfc" }}>
              <Typography variant="caption" color="text.secondary">
                {t("legend", "legend").toLowerCase()}:{" "}
                <Box component="span" sx={{ color: "success.main", fontWeight: 800 }}>P</Box> {t("PRESENT", "PRESENT").toUpperCase()},{" "}
                <Box component="span" sx={{ color: "error.main", fontWeight: 800 }}>A</Box> {t("ABSENT", "ABSENT").toUpperCase()},{" "}
                <Box component="span" sx={{ color: "warning.main", fontWeight: 800 }}>L</Box> {t("LEAVE", "LEAVE").toUpperCase()},{" "}
                <Box component="span" sx={{ color: "info.main", fontWeight: 800 }}>H</Box> {t("half_day", "Half Day")}
              </Typography>
            </Box>
          </Paper>
        </Box>
    </CommonModal>
  );
};

export default AttendanceFormModal;